import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet} from 'react-helmet';
import {logPageLoadEvents} from '@ibibo/analytics';
import {perfUtils} from '@ibibo/hotel-utils';
import {gaConfig} from '@ibibo/hotels-config';
import {userSettingDataSelector} from '@ibibo/myaccount-reducer';

import * as S from './DetailsPage.styles';
import {
  Amenities,
  Breadcrumb,
  HotelDetails,
  LocationMap,
  Navigation,
  RoomSelection,
  // SimilarHotels,
  VoyagerStatusHandler,
} from './components';
import {sendEvarData} from '@ibibo/analytics';

import {
  fetchDetailsStaticDataThunk,
  fetchDetailsHermesDataThunk,
  fetchHotelPolicyDataThunk,
  fetchSimilarHotelsThunk,
} from './redux/thunks';
import {
  hasStaticDataSelector,
  detailSelectorFn,
  hasHermesDataBooleanSelector,
} from './selectors/index';
import {commonSelector} from '../SRP/redux/selectors';

import SearchWidget from '../SRP/containers/SearchWidget';

// import SmartEngageApiContainer from '../SmartEngage/SmartEngageApiContainer';

// import AboutHotel from '../../../stories/components/hotels/DetailsPage/AboutHotel/AboutHotel';

function getFormatDate(date) {
  var d = date.date.getDate();
  var m = date.date.getMonth() + 1;
  var y = date.date.getFullYear();
  return (d <= 9 ? '0' + d : d) + ':' + (m<=9 ? '0' + m : m) + ':' + y;
}

function formatRoomString(roomString) {
  var parts = roomString.split("-")
  return `mypartner:${parts[1]}A|${parts[2]}C|0I|${parseInt(parts[1]) + parseInt(parts[2])}`
}

const DetailsPage = () => {
  const dispatch = useDispatch();
  const hasStaticData = useSelector(hasStaticDataSelector);
  const hasHermesData = useSelector(hasHermesDataBooleanSelector);
  const detail = useSelector(detailSelectorFn);
  const userData = useSelector(userSettingDataSelector);
  const { account_id } = userData;
  const common = useSelector(commonSelector);
  const { checkin, checkout, destination, country } = common;
  const { vhid } = detail;


  const {staticData = {}} = detail;
  const {seo_markup_data: seoData = {}} = staticData;

  /**
   * ***********FLOW OF THIS USE EFFECT ****************
   *
   * fetchNeededDetailsData is an async function to colocate all the thunk dispatches together
   * instead of firing them from one inside the other
   *
   * if they are called from one inside the other, it's difficult to track what is happening where and when
   * .
   * In contrast now this is the flow —
   *
   *   fetchDetailsStaticDataThunk dispatch successfully fetches static data and updates hasStaticData,
   *   which then prompts fetchDetailsHermesDataThunk to get dispatched, and on its success
   *   hotel policy is fetched because it relies on both static and hermes data.
   *
   * The first approach and second approach triggers same number of re renders, because hasStaticData
   * and hasHermesData is accessed from redux state.
   */

   const htlName = detail.selectedHotelStatic.hn.toUpperCase();
  useEffect(() => {
    async function fetchNeededDetailsData() {
      if (!hasStaticData) {
        await dispatch(fetchDetailsStaticDataThunk());
      }
      if (hasStaticData && !hasHermesData) {
        perfUtils.markPerformanceStart(gaConfig.DetailPageGAEvents.performanceMarkers.start);
        await dispatch(fetchDetailsHermesDataThunk());
      }
    }
    fetchNeededDetailsData();
  }, [hasStaticData, hasHermesData]);

  let searchType = 'domestic';
  if(detail.staticData.cn !== 'India'){
    searchType = 'intl';
  }

  useEffect(() => {
    if(account_id && hasHermesData && vhid) {
      var date_today = new Date();
      var diff = Math.abs(checkin.date.getTime() - date_today.getTime());
      var diffDays = Math.ceil(diff / (1000 * 3600 * 24)); 
      sendEvarData({
        eVar40: destination.n,
        prop24: `${country === "india" ? "domestic" : "international"} hotels|mypartner`,
        eVar24: "D=c24",
        eVar37: "domain_sbu:india|domain_country:in|domain_currency:inr|domain_language:eng",
        eVar4: "D=c4",
        eVar5: "D=c5",
        eVar7: "D=c7",
        eVar67: account_id ? account_id.value : "",
        prop4: getFormatDate(checkin),
        prop5: getFormatDate(checkout),
        prop7: diffDays,
        eVar11: "D=c11",
        prop11: formatRoomString(common.roomString),
      }, false, {products: vhid, pageName: `funnel:${searchType} hotels:hoteldetails|mypartner`})
    }  
  }, [hasHermesData])
  
  /** independently fetch data that is needed after static data and hermes */
  useEffect(() => {
    if (hasStaticData && hasHermesData) {
      dispatch(fetchHotelPolicyDataThunk());
      // dispatch(fetchSimilarHotelsThunk());
    }
  }, [hasStaticData, hasHermesData]);

  if (seoData.link && seoData.link.length) {
    seoData.link.forEach(item => {
      if (item.rel === 'shortcut icon') {
        item.href = 'https://imgak.mmtcdn.com/favicon.ico';
      }
    })
  }

  return (
    <>
      <Helmet title={'myPartner-'  + htlName + '-Hotel Details'} meta={seoData.meta} link={seoData.link} />
      <S.DetailCardInnerSection>

        <VoyagerStatusHandler />

        <SearchWidget isDetailsPage />

        {/* <SmartEngageApiContainer page="Details" /> */}

        <S.DetailMainWrapper>

          <S.DetailMainInnerWrapper>
            <Breadcrumb />
            <HotelDetails />
          </S.DetailMainInnerWrapper>
          
          <Navigation />

          <RoomSelection searchType={searchType}/>
          
          <Amenities />

          <LocationMap />

          {/* <SimilarHotels /> */}

        </S.DetailMainWrapper>

      </S.DetailCardInnerSection>
      
    </>
  );
};

export default DetailsPage;
